import * as React from 'react';
import { Suspense, useCallback, useEffect, useState } from 'react';
import { useAtom } from 'jotai';
import QRCode from 'qrcode.react';
import { qrAtom } from '@src/stores/qr';
import ProgressBalls from '@components/common/ProgressBalls';
import { showSimpleToast } from '@utils/toastUtils';
import useUserInfoStore from '@hooks/useUserInfoStore';
import {
  BackgroundWrap,
  CancelWrap,
  DimmedWrap,
  QRBorderWrap,
  QRWrap,
  UniqueCodeWrap,
} from '@components/modal/styles/qrModal.style';
import { postAppMessage } from '@utils/appMessage';
import usersQuery from '@src/apis/querys/usersQuery';
import { CMD } from '@src/constants/appMessageCmd';
import Text from '@components/common/Text';
import Spacing from '@components/common/Spacing';
import { googleTagManager } from '@src/tags';
import Icon from '@src/svg/Icon';

const QRComponent = () => {
  const { data, isPending } = usersQuery.useGetMyUniqueCode();

  return (
    <QRWrap>
      {/* 토큰 주입 전(query disabled)·조회 중에는 빈 화면 대신 로더를 보여준다 (v5 에서 suspense 옵션 제거로 폴백이 닿지 않음).
          실패 시에는 로더가 계속 돌지 않도록 조회 중일 때만 표시한다. */}
      {!data && isPending && <ProgressBalls />}
      {data && (
        <>
          <Text typo="heading1_semiBold" color="gray100">
            적립 QR코드
          </Text>
          <Spacing size={8} />
          <Text typo="body2_medium" color="gray60" textAlign="center">
            포토부스 카메라에
            <br />
            QR코드를 인식해 주세요!
          </Text>
          <QRBorderWrap>
            <QRCode id={data} value={data} size={200} />
          </QRBorderWrap>
          <UniqueCodeWrap>
            <Text typo="body3_semiBold">QR번호</Text>
            {data.replace(/(.{4})/g, '$1 - ').replace(/ - $/g, '')}
          </UniqueCodeWrap>
        </>
      )}
    </QRWrap>
  );
};

const QRModal = (): JSX.Element => {
  const [isModal, setIsModal] = useState(false);
  const [isAtomModal, setIsAtomModal] = useAtom(qrAtom);
  const [userData] = useUserInfoStore();

  useEffect(() => {
    if (!userData?.userId) {
      setIsAtomModal(false);
      showSimpleToast({
        type: 'error',
        content: '로그인 후 이용 가능합니다.',
      });
    }
  }, [userData?.userId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    postAppMessage({
      cmd: CMD.BARCODE_MODE,
      bright: isModal,
    });
  }, [isModal]);

  useEffect(() => {
    if (isAtomModal) {
      setIsModal(true);
      googleTagManager.QR_Popup_View();
    }
  }, [isAtomModal]);

  const closeModal = useCallback(() => {
    setIsModal(false);
    setTimeout(() => {
      setIsAtomModal(false);
    }, 300);
  }, [isModal, isAtomModal]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <DimmedWrap className={isModal ? 'up' : 'down'} />
      <BackgroundWrap className={isModal ? 'up' : 'down'}>
        <Suspense fallback={<ProgressBalls />}>
          <QRComponent />
        </Suspense>
        <CancelWrap onClick={closeModal}>
          <Icon name="return" width={14} height={14} className="cancel-icon" />
          <Text typo="body1_medium" color="gray20" className="underline">
            이전 화면으로 가기
          </Text>
        </CancelWrap>
      </BackgroundWrap>
    </>
  );
};

export default QRModal;
